// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module Timeline
 * @package @geenius/analytics/solidjs
 * @description Renders the SolidJS analytics timeline for recent event
 * activity. The component maps shared timeline events into a compact log view
 * with event icons and localized timestamps.
 */

import { EVENT_ICONS, type TimelineEvent } from "@geenius/analytics";
import { Card, CardContent } from "@geenius/ui/solidjs";
import { type Component, For } from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/**
 * Props for rendering the SolidJS analytics timeline.
 *
 * @property events Timeline events to render in descending recency order.
 * @property maxVisible Maximum number of timeline rows to display.
 * @property error Optional error value rendered instead of the timeline.
 * @property onRetry Optional recovery callback shown with the error state.
 * @property class Optional class name merged into the timeline wrapper.
 */
export interface TimelineProps {
  events: TimelineEvent[];
  maxVisible?: number;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  class?: string;
}

/**
 * Renders a SolidJS timeline for recent analytics events.
 *
 * @param props Timeline events and the optional visible-row limit.
 * @returns A timeline log element for analytics activity.
 */
export const Timeline: Component<TimelineProps> = (props) => {
  const copy = createAnalyticsCopy();
  const visible = () => props.events.slice(0, props.maxVisible ?? 20);

  if (props.error) {
    return (
      <Card class={props.class}>
        <CardContent class="pt-6">
          <ErrorState error={props.error} onRetry={props.onRetry} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card class={props.class}>
      <CardContent class="pt-6">
        <div class="flex flex-col gap-2" data-analytics="timeline" role="log">
          <For each={visible()}>
            {(event) => (
              <div
                class="flex items-center gap-3 rounded-xl bg-muted px-3 py-3"
                data-analytics="timeline-event"
              >
                <span data-analytics="timeline-icon">
                  {EVENT_ICONS[event.event] ?? "⚡"}
                </span>
                <span class="font-medium" data-analytics="timeline-name">
                  {event.event}
                </span>
                {event.userId && (
                  <span
                    class="text-sm text-muted-foreground"
                    data-analytics="timeline-user"
                  >
                    {event.userId}
                  </span>
                )}
                <span
                  class="ml-auto text-sm text-muted-foreground"
                  data-analytics="timeline-time"
                >
                  {copy.formatTime(event.timestamp)}
                </span>
              </div>
            )}
          </For>
        </div>
      </CardContent>
    </Card>
  );
};
